import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  type DetailedHTMLProps,
  type HTMLAttributes,
  type Ref,
} from 'react';

import type {
  JaakStampsElement,
  JaakCaptureResult,
  JaakCaptureSide,
  JaakLicenseEnvironment,
  JaakCaptureError,
} from '@tankef/core';

import { Alert } from '../../primitives/Alert';
import { Button } from '../../primitives/Button';
import { Spinner } from '../../primitives/Spinner';
import { cn } from '../../utils';
import { JaakDocumentCaptureStub } from '../_e2e-stubs/JaakDocumentCaptureStub';

// Plan 08 F1: VITE_E2E es leído de import.meta.env (Vite). En tests E2E,
// playwright.config.ts setea VITE_E2E=true antes de boot del dev server.
// El packages/ui no tiene typing para import.meta.env (vive en apps/web),
// así que casteamos al shape mínimo necesario.
//
// 2026-05-29: agregado `VITE_KYC_LOCAL_STUB` para que dev local pueda
// montar el stub sin abusar de la var de E2E. JAAK rechaza requests CORS
// desde localhost; tener un flag explícito permite trabajar el flujo INE/
// selfie en dev sin pelear con eso, mientras staging/prod sigue usando el
// SDK real. Ambas vars OR-ean al mismo branch.
const E2E_MODE = (() => {
  const env = (
    import.meta as unknown as { env?: { VITE_E2E?: string; VITE_KYC_LOCAL_STUB?: string } }
  ).env;
  return env?.VITE_E2E === 'true' || env?.VITE_KYC_LOCAL_STUB === 'true';
})();

// ============================================================================
// JSX Declaration for WebComponent
// ============================================================================

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'jaak-stamps': DetailedHTMLProps<
        HTMLAttributes<JaakStampsElement> & {
          license?: string;
          'license-environment'?: JaakLicenseEnvironment;
          'app-id'?: string;
          'mask-size'?: number;
          'alignment-tolerance'?: number;
          'crop-margin'?: number;
          'capture-delay'?: number;
          debug?: boolean;
          'preferred-camera'?: 'auto' | 'front' | 'back';
          'use-document-classification'?: boolean;
          'enable-telemetry'?: boolean;
          'enable-metrics'?: boolean;
          ref?: Ref<JaakStampsElement>;
        },
        JaakStampsElement
      >;
    }
  }
}

// ============================================================================
// Constants
// ============================================================================

const JAAK_SDK_URL =
  'https://unpkg.com/@jaak.ai/stamps@2.5.8/dist/jaak-stamps-webcomponent/jaak-stamps-webcomponent.esm.js';
// SHA-384 of the pinned bundle. Verified against the upstream CDN on
// 2026-09-11 (2.5.8); rotate together with the version above. If the CDN serves
// modified bytes the browser will refuse to execute, which is the whole
// point of pinning a third-party identity-capture SDK.
const JAAK_SDK_INTEGRITY =
  'sha384-FgkjMtVxi4EKVPNGK8n6u0ZZdpsaip+5AbaUD8HkjRiMQSD7PP8hhmG/O09L50vu';

/**
 * Tope por INACTIVIDAD del sondeo de `getStatus()`. Sin él el intervalo de
 * 400 ms corre indefinidamente: si el SDK se queda colgado en `capturing`
 * (cámara tomada por otra pestaña, modelo que no carga) la pantalla nunca sale
 * de ahí. El contador se reinicia cada vez que el SDK avanza de lado
 * (front → back → completed): un usuario lento no pierde la captura, sólo un
 * visor que no progresa en 90 s. El aviso sale por `onError`, el mismo canal
 * de los errores del SDK.
 */
const CAPTURE_STATUS_POLL_MS = 400;
const CAPTURE_STALL_TIMEOUT_MS = 90_000;

// ============================================================================
// Types
// ============================================================================

export type JaakCaptureStep =
  | 'loading' // Cargando SDK
  | 'ready' // Listo para capturar
  | 'capturing' // Captura en progreso (front + back los maneja el SDK internamente)
  | 'complete' // Captura exitosa
  | 'error'; // Error

export interface JaakDocumentCaptureLabels {
  loading?: string;
  readyTitle?: string;
  readyDescription?: string;
  capturingTitle?: string;
  capturingDescription?: string;
  /**
   * Textos por cara durante la captura. Si no se pasan, o si el SDK no reporta
   * la cara, se usa `capturingTitle`/`capturingDescription` (neutral).
   */
  capturingFrontTitle?: string;
  capturingFrontDescription?: string;
  capturingBackTitle?: string;
  capturingBackDescription?: string;
  errorTitle?: string;
  retryButton?: string;
  startButton?: string;
  cancelButton?: string;
  /** Mensaje cuando la captura se queda colgada y se corta por tiempo. */
  timeoutError?: string;
}

export interface JaakDocumentCaptureHandle {
  startCapture: () => void;
  cancel: () => void;
}

export interface JaakDocumentCaptureProps {
  /** License key de Jaak (requerido) */
  licenseKey: string;
  /** Ambiente de la licencia (requerido — no asumir 'sandbox' silenciosamente) */
  licenseEnvironment: JaakLicenseEnvironment;
  /** Callback cuando la captura se completa exitosamente */
  onCaptureComplete: (images: JaakCaptureResult) => void;
  /** Callback cuando ocurre un error */
  onError?: (error: string) => void;
  /** Callback para cancelar */
  onCancel?: () => void;
  /** Notifica al padre cuando el step interno cambia */
  onStepChange?: (step: JaakCaptureStep) => void;
  /** Si true, oculta los botones internos (el padre controla las acciones via ref) */
  hideActions?: boolean;
  /**
   * Textos personalizables. Los defaults son genéricos en español ("documento")
   * pensados como fallback de desarrollo — apps en producción deberían pasar
   * todos los labels traducidos via i18n.
   */
  labels?: JaakDocumentCaptureLabels;
  /** Clases CSS adicionales */
  className?: string;

  // ── Web component config (mapeados a atributos de <jaak-stamps>) ─────────
  /** Tamaño de la máscara (porcentaje 0–100). Default 90. */
  maskSize?: number;
  /** Tolerancia de alineación. Default 15. */
  alignmentTolerance?: number;
  /** Margen del crop. Default 20. */
  cropMargin?: number;
  /** Delay antes de capturar (ms). Default 1500. */
  captureDelay?: number;
  /** Cámara preferida. Default 'back'. */
  preferredCamera?: 'auto' | 'front' | 'back';
  /** Debug del SDK. */
  debug?: boolean;
  /** App ID de Jaak. */
  appId?: string;
  /**
   * Clasificación de documento del SDK (`use-document-classification`): permite
   * al SDK rechazar el segundo anverso ANTES de subirlo, en vez de gastar un
   * contrato con el proveedor para que el backend lo rechace por caras.
   *
   * Sin valor, el atributo no se emite y manda el default del SDK. El step lo
   * enciende con la bandera `jaakDocumentClassificationEnabled` de `@tankef/env`
   * (`VITE_JAAK_DOCUMENT_CLASSIFICATION_ENABLED`), porque su efecto real sólo se
   * ve en un ambiente desplegado (JAAK rechaza CORS desde localhost).
   */
  useDocumentClassification?: boolean;
  /** Habilitar telemetría del SDK. Default false. */
  enableTelemetry?: boolean;
  /** Habilitar métricas del SDK. Default false. */
  enableMetrics?: boolean;
}

// ============================================================================
// Default Labels — fallback de desarrollo. Los callers en producción deben
// pasar `labels` con strings traducidos via i18n.
// ============================================================================

const defaultLabels: Required<JaakDocumentCaptureLabels> = {
  loading: 'Cargando cámara...',
  readyTitle: 'Captura tu documento',
  readyDescription: 'Asegúrate de tener buena iluminación y que el documento sea legible',
  capturingTitle: 'Captura en progreso',
  capturingDescription: 'Sigue las indicaciones en pantalla para capturar el documento',
  capturingFrontTitle: '',
  capturingFrontDescription: '',
  capturingBackTitle: '',
  capturingBackDescription: '',
  errorTitle: 'Error al capturar',
  retryButton: 'Reintentar',
  startButton: 'Iniciar captura',
  cancelButton: 'Cancelar',
  timeoutError: 'La captura tardó demasiado. Vuelve a intentarlo.',
};

/**
 * Extrae la cara en curso del snapshot que resuelve `getStatus()`.
 *
 * Si el shape no es el esperado devuelve `null`, y el componente se queda con
 * el texto neutral en vez de anunciar una cara equivocada: `captureStep` no
 * está documentado por JAAK, sólo confirmado por escrito.
 */
function readCaptureSide(status: unknown): JaakCaptureSide | null {
  if (!status || typeof status !== 'object') return null;
  const step = (status as { captureStep?: unknown }).captureStep;
  return step === 'front' || step === 'back' || step === 'completed' ? step : null;
}

// ============================================================================
// SDK Loader
// ============================================================================

let sdkLoadPromise: Promise<void> | null = null;

function loadJaakSdk(): Promise<void> {
  if (sdkLoadPromise) return sdkLoadPromise;

  sdkLoadPromise = new Promise((resolve, reject) => {
    if (customElements.get('jaak-stamps')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = JAAK_SDK_URL;
    script.integrity = JAAK_SDK_INTEGRITY;
    script.crossOrigin = 'anonymous';
    script.onload = () => resolve();
    script.onerror = () => {
      // Reset so a future mount can retry the load instead of inheriting
      // the rejected promise forever.
      sdkLoadPromise = null;
      reject(new Error('Failed to load Jaak SDK'));
    };
    document.head.appendChild(script);
  });

  return sdkLoadPromise;
}

// ============================================================================
// Component
// ============================================================================

const JaakDocumentCaptureReal = forwardRef<JaakDocumentCaptureHandle, JaakDocumentCaptureProps>(
  function JaakDocumentCapture(
    {
      licenseKey,
      licenseEnvironment,
      onCaptureComplete,
      onError,
      onCancel,
      onStepChange,
      hideActions = false,
      labels: customLabels,
      className,
      maskSize = 90,
      alignmentTolerance = 15,
      cropMargin = 20,
      captureDelay = 1500,
      preferredCamera = 'back',
      debug,
      appId,
      useDocumentClassification,
      enableTelemetry = false,
      enableMetrics = false,
    }: JaakDocumentCaptureProps,
    ref
  ) {
    const jaakRef = useRef<JaakStampsElement | null>(null);
    const [step, setStep] = useState<JaakCaptureStep>('loading');
    // Cara en curso. Estado LOCAL a propósito: no se propaga al padre, porque
    // hacerlo rompe el ciclo de vida del webcomponent (ver `onStepChange`).
    const [side, setSide] = useState<JaakCaptureSide | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [sdkLoaded, setSdkLoaded] = useState(false);

    const labels = { ...defaultLabels, ...customLabels };
    // Valor (string), no referencia: se puede poner en las deps del efecto del
    // sondeo sin re-armar el intervalo en cada render.
    const timeoutMessage = labels.timeoutError;
    // Refs: el intervalo no debe rearmarse (y reiniciar su contador) cada vez
    // que el padre re-renderiza con un `onError` nuevo.
    const onErrorRef = useRef(onError);
    const timeoutMessageRef = useRef(timeoutMessage);
    useEffect(() => {
      onErrorRef.current = onError;
      timeoutMessageRef.current = timeoutMessage;
    }, [onError, timeoutMessage]);

    useEffect(() => {
      loadJaakSdk()
        .then(() => {
          setSdkLoaded(true);
        })
        .catch(err => {
          setStep('error');
          setErrorMessage(err.message || 'Error al cargar el SDK');
          onError?.(err.message);
        });
    }, [onError]);

    const handleReady = useCallback(() => {
      setStep('ready');
    }, []);

    // Safely remove the Jaak Stamps webcomponent from DOM.
    // Jaak Stamps <= 2.5.5 throws "Service detectionService not found" in
    // disconnectedCallback/stopCapture if detection was never fully initialized.
    // 2.5.6+ silences the related console warnings, but we keep the try/catch
    // as defense-in-depth — element.remove() should never throw, but the
    // SDK's lifecycle hooks have historically been unstable.
    const safeStop = useCallback(() => {
      if (!jaakRef.current) return;
      try {
        jaakRef.current.remove();
      } catch {
        // Absorb SDK disconnectedCallback errors
      }
      jaakRef.current = null;
    }, []);

    const handleCaptureComplete = useCallback(
      (event: Event) => {
        const customEvent = event as CustomEvent<JaakCaptureResult>;
        safeStop();
        setStep('complete');
        onCaptureComplete(customEvent.detail);
      },
      [onCaptureComplete, safeStop]
    );

    const handleError = useCallback(
      (event: Event) => {
        const customEvent = event as CustomEvent<JaakCaptureError>;
        safeStop();
        setStep('error');
        setErrorMessage(customEvent.detail.message);
        onError?.(customEvent.detail.message);
      },
      [onError, safeStop]
    );

    const handleCancel = useCallback(() => {
      safeStop();
      onCancel?.();
    }, [onCancel, safeStop]);

    useEffect(() => {
      if (!sdkLoaded) return;

      const element = jaakRef.current;
      if (!element) return;

      element.addEventListener('isReady', handleReady);
      element.addEventListener('captureCompleted', handleCaptureComplete);
      element.addEventListener('error', handleError);

      return () => {
        element.removeEventListener('isReady', handleReady);
        element.removeEventListener('captureCompleted', handleCaptureComplete);
        element.removeEventListener('error', handleError);
        safeStop();
      };
    }, [sdkLoaded, handleReady, handleCaptureComplete, handleError, safeStop]);

    const startCapture = useCallback(() => {
      setStep('capturing');
      setSide(null);
      jaakRef.current?.startCapture();
    }, []);

    // No hay evento de cambio de cara (los únicos son isReady, modelsLoaded,
    // captureCompleted y error), así que se consulta `getStatus()`. Es barato y
    // silencioso; `getCapturedImages()` no sirve para esto — a media captura
    // registra un error por llamada y nunca devuelve parciales.
    useEffect(() => {
      if (step !== 'capturing') return;
      let cancelled = false;
      let elapsed = 0;
      let lastSide: JaakCaptureSide | null = null;
      // `getStatus()` es ASÍNCRONO: devuelve una promesa que resuelve al
      // snapshot. Leer la promesa como si fuera el objeto deja `captureStep`
      // fuera de alcance.
      const readSide = async () => {
        const element = jaakRef.current;
        if (!element) return;
        try {
          const snapshot = await element.getStatus?.();
          if (cancelled) return;
          const next = readCaptureSide(snapshot);
          if (next && next !== lastSide) {
            // Avance real del SDK: el tope por inactividad vuelve a cero.
            lastSide = next;
            elapsed = 0;
          }
          setSide(prev => (next && next !== prev ? next : prev));
        } catch {
          // Absorber: el SDK ha lanzado desde sus getters en otras versiones.
        }
      };
      void readSide();
      const timer = setInterval(() => {
        elapsed += CAPTURE_STATUS_POLL_MS;
        if (elapsed >= CAPTURE_STALL_TIMEOUT_MS) {
          // Se corta el sondeo Y la captura: dejar el intervalo vivo tras el
          // tope sólo cambia un visor muerto por un visor muerto que consume
          // batería. El aviso sale por `onError`, igual que los errores del SDK.
          cancelled = true;
          clearInterval(timer);
          safeStop();
          setStep('error');
          setErrorMessage(timeoutMessageRef.current);
          onErrorRef.current?.(timeoutMessageRef.current);
          return;
        }
        void readSide();
      }, CAPTURE_STATUS_POLL_MS);
      return () => {
        cancelled = true;
        clearInterval(timer);
      };
    }, [step, safeStop]);

    useImperativeHandle(
      ref,
      () => ({
        startCapture,
        cancel: handleCancel,
      }),
      [startCapture, handleCancel]
    );

    useEffect(() => {
      onStepChange?.(step);
    }, [step, onStepChange]);

    const handleRetry = () => {
      setErrorMessage('');
      jaakRef.current?.resetCapture();
      startCapture();
    };

    const isLoadingState = step === 'loading';

    return (
      <div className={cn('flex flex-col items-center', className)}>
        {/* Status Header — aria-live announces transitions to AT users */}
        <div
          className="mb-6 text-center"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          aria-busy={isLoadingState}
        >
          {step === 'loading' && (
            <>
              <Spinner size="lg" className="mx-auto mb-4" />
              <p className="text-tkf-fg-muted">{labels.loading}</p>
            </>
          )}

          {step === 'ready' && (
            <>
              <h2 className="mb-2 text-xl font-bold text-tkf-fg">{labels.readyTitle}</h2>
              <p className="text-sm text-tkf-fg-muted">{labels.readyDescription}</p>
            </>
          )}

          {step === 'capturing' && (
            <>
              <h2 className="mb-2 text-xl font-bold text-tkf-fg">
                {(side === 'front' && labels.capturingFrontTitle) ||
                  (side === 'back' && labels.capturingBackTitle) ||
                  labels.capturingTitle}
              </h2>
              <p className="text-sm text-tkf-fg-muted">
                {(side === 'front' && labels.capturingFrontDescription) ||
                  (side === 'back' && labels.capturingBackDescription) ||
                  labels.capturingDescription}
              </p>
            </>
          )}

          {step === 'error' && (
            <Alert variant="error" title={labels.errorTitle} className="mx-auto max-w-md text-left">
              {errorMessage}
            </Alert>
          )}
        </div>

        {/* WebComponent Container */}
        {sdkLoaded && step !== 'error' && (
          <div
            className={cn(
              'relative aspect-[4/3] w-full max-w-md overflow-hidden rounded-lg bg-black',
              isLoadingState && 'animate-pulse'
            )}
          >
            <jaak-stamps
              ref={jaakRef}
              license={licenseKey}
              license-environment={licenseEnvironment}
              {...(appId !== undefined ? { 'app-id': appId } : {})}
              mask-size={maskSize}
              alignment-tolerance={alignmentTolerance}
              crop-margin={cropMargin}
              capture-delay={captureDelay}
              preferred-camera={preferredCamera}
              {...(debug !== undefined ? { debug } : {})}
              {...(useDocumentClassification !== undefined
                ? { 'use-document-classification': useDocumentClassification }
                : {})}
              enable-telemetry={enableTelemetry}
              enable-metrics={enableMetrics}
            />
          </div>
        )}

        {/* Action Buttons — hidden when parent controls them via hideActions */}
        {!hideActions && (
          <div className="mt-6 flex w-full max-w-md gap-4">
            {step === 'ready' && (
              <>
                {onCancel && (
                  <Button variant="outline" className="flex-1" onClick={handleCancel}>
                    {labels.cancelButton}
                  </Button>
                )}
                <Button variant="primary" className="flex-1" onClick={startCapture}>
                  {labels.startButton}
                </Button>
              </>
            )}

            {step === 'error' && (
              <>
                {onCancel && (
                  <Button variant="outline" className="flex-1" onClick={handleCancel}>
                    {labels.cancelButton}
                  </Button>
                )}
                <Button variant="primary" className="flex-1" onClick={handleRetry}>
                  {labels.retryButton}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

// Plan 08 F1: export condicional resuelto a nivel de módulo (NO en render)
// para no violar Rules of Hooks. En builds de prod (VITE_E2E unset) Vite
// tree-shakea el branch + el import del stub. En tests E2E, el componente
// real nunca se monta — se sustituye por la versión stub controlable.
const JaakDocumentCaptureE2EWrapper = forwardRef<
  JaakDocumentCaptureHandle,
  JaakDocumentCaptureProps
>(function JaakDocumentCaptureE2EWrapper(props, _ref) {
  return <JaakDocumentCaptureStub {...props} />;
});

export const JaakDocumentCapture = E2E_MODE
  ? JaakDocumentCaptureE2EWrapper
  : JaakDocumentCaptureReal;
